/**
 * Lightweight debug logger.
 *
 * Gates verbose, per-frame/per-event logging behind a single flag so production
 * builds stay quiet while debug builds remain informative.
 *
 * Default: enabled in dev (`import.meta.env.DEV`), disabled in production builds.
 *
 * Runtime override (e.g. from the browser console):
 *   window.__motionArtDebug = true;   // force-enable
 *   window.__motionArtDebug = false;  // force-disable
 *
 * Note: `console.error` is intentionally NOT gated elsewhere — genuine errors
 * should always surface. This logger is for diagnostic/informational output.
 */

const RUNTIME_FLAG = '__motionArtDebug';

/**
 * Resolve whether debug logging is currently enabled.
 * A runtime override on `window` takes precedence over the build-time default.
 */
function isDebugEnabled(): boolean {
  if (typeof window !== 'undefined') {
    const override = (window as any)[RUNTIME_FLAG];
    if (typeof override === 'boolean') {
      return override;
    }
  }

  // Vite exposes import.meta.env.DEV; fall back to false if unavailable (e.g. tests).
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

export const logger = {
  /** Informational/diagnostic logging. Suppressed unless debug is enabled. */
  log(...args: unknown[]): void {
    if (isDebugEnabled()) {
      console.log(...args);
    }
  },

  /** Warnings. Suppressed unless debug is enabled. */
  warn(...args: unknown[]): void {
    if (isDebugEnabled()) {
      console.warn(...args);
    }
  },

  /** Grouped logging helper. Suppressed unless debug is enabled. */
  group(label: string, body: () => void): void {
    if (!isDebugEnabled()) {
      return;
    }
    console.group(label);
    try {
      body();
    } finally {
      console.groupEnd();
    }
  },
};
